/**
 * Reject kitchen layouts whose cabinets / appliances on one wall stick
 * into the clear-floor zone in front of a door, passage opening, or
 * window on an adjoining wall. Without this check, a 24"-deep cabinet
 * placed flush at a corner can land directly in the path of a door that
 * opens on the perpendicular wall a couple of feet from the corner.
 *
 * Same-wall openings are NOT checked here — `wallSegments.js` already
 * keeps cabinets out of the door / opening width along the same wall.
 */

const { getWallAxes } = require('./wallMath');
const { getFloorY } = require('./floorY');
const { isLowSillWindow } = require('./candidates/windowBlocking');
const {
  calculateRectCorners,
  doPolygonsOverlap,
} = require('../bathroom-analyzer/pipeline/geometry');
const {
  DOOR_FRONT_CLEARANCE,
  OPENING_FRONT_CLEARANCE,
  WINDOW_FRONT_CLEARANCE,
  OPENING_PASSAGE_CLEARANCE,
} = require('./constants');

/**
 * Does this door's leaf travel INSIDE the opening (or into the wall) rather
 * than sweeping into the room? RoomPlan spells the type out in
 * `category.door.doorType` — `leftHandleSliding`, `rightHandleSliding`,
 * pocket variants — and `openingSync` carries that field onto the room-side
 * copy, so a hydrated room door answers this too. Anything else (inswing,
 * outswing, French) hinges and keeps the width-as-swing-radius rule.
 */
function isSlidingDoor(opening) {
  const doorType = opening?.category?.door?.doorType;
  return typeof doorType === 'string' && /sliding|pocket/i.test(doorType);
}

/** Items "tall enough to matter" for opening-clearance checks.
 *
 * The 30" stand-back in front of doors/openings/windows is about
 * (a) door swing arc, (b) traffic flow at standing-eye level, and
 * (c) visual breathing room around the casing. None of those are
 * meaningfully impeded by a 24"-deep base cabinet on an adjoining
 * wall — base cabs sit BELOW the door swing arc, BELOW the window
 * sill, and don't constrict standing-level traffic. The check
 * matters for items whose body extends ABOVE counter (~36") into
 * standing-eye / door-swing space: fridges, wall ovens, range
 * hoods, upper cabinets, fridge surrounds.
 *
 * Counter-height items (lower cabinets, dishwashers, base-mount
 * stoves/ovens, sinks) are intentionally NOT checked — they're
 * allowed to sit at the corner of an adjoining wall even when the
 * 24"-deep base footprint technically grazes a clearance zone.
 * This matches how real kitchen designers treat the rule. */
const TALL_TYPES = new Set([
  'fridge',
  'refrigerator',
  'fridge-surround',
  'wall-oven',
  'wallOven',
  'upper-cabinet',
  // Both old (`range-hood`) and new (`vent-hood`) names — saved structures
  // from before the type-name alignment keep the old name; new emits use
  // the new name.
  'range-hood',
  'vent-hood',
]);
/** @deprecated kept for backwards compat; new code should use TALL_TYPES. */
const BLOCKING_TYPES = TALL_TYPES;

// Tolerance for opening-clearance overlap checks. Bumped from 10mm to
// 30mm to absorb "tight-squeeze" cases where a clearance polygon JUST
// barely clips into an adjoining wall's cabinet-depth strip — e.g. a
// window 23.4" from a perpendicular wall projects a blocker that
// wipes out 17" of fill cabinets when the actual cabinet body would
// brush the standing-clearance zone by less than ¾". Real designers
// build to ½–1" of these polygons. Genuine intrusions (>30mm) still
// produce a blocker.
const OVERLAP_TOLERANCE = 0.03;

/**
 * Build one clearance zone polygon per door / opening / window on the
 * room's walls. Each zone is a rectangle whose back face sits flush
 * against the wall surface and whose front face extends `clearance`
 * meters into the room along the wall's inward normal. Width = the
 * opening's along-wall width.
 */
function getOpeningClearanceZones(roomData, options = {}) {
  const walls = roomData?.walls || [];
  const wallById = new Map(walls.map((w) => [w.id, w]));
  // Counter-height obstruction test for each zone (see `blocksCounterHeight`
  // below). A room with no floor geometry gives `getFloorY` its 0 fallback,
  // which would misread every sill — so only judge sills when the room
  // actually carries a floor, and treat every zone as blocking otherwise.
  const sillsAreKnowable = (roomData?.floors || []).length > 0;
  const floorY = getFloorY(roomData);
  // Door swing-only mode: use each door's OWN WIDTH as the clearance
  // depth instead of the padded 54" stand-back. A door of width W sweeps
  // a quarter-circle of radius W, so W is the exact bounding depth of the
  // actual leaf swing — everything beyond it is approach/traffic buffer
  // the door never reaches. The peninsula pre-filter passes this so a
  // counter-height run may sit in the buffer (openness is preserved at
  // counter height) while still being blocked from the real swing /
  // doorway passage. The 54" zone is retained for the tall-item checks.
  //
  // ...but width IS the swing radius only for a hinged leaf. A SLIDING or
  // POCKET door sweeps nothing: its panels travel inside the opening (or
  // into the wall), so what has to stay clear in front of it is the
  // PASSAGE, exactly as for a cased opening — hence
  // `OPENING_FRONT_CLEARANCE`, not the width. Charging a slider its own
  // width is worst precisely where sliders are widest: every door in the
  // corpus over 75" is a slider, and a 197" patio slider at Golden West,
  // Temple City projected a 197"-DEEP zone across the whole kitchen. That
  // zone lapped the far end of the perpendicular cabinet wall, so
  // `peninsulaAnchorOffsets` slid the floating-U's arm 223" inboard on a
  // 337" wall — a peninsula rooted mid-run, and (past the wall midpoint)
  // one whose back arm then faced out of the U. The 54" tall-item
  // stand-back is deliberately unchanged: a fridge in front of a patio
  // slider is still wrong.
  // `frontClearance` sets ONE depth for every kind — the run fill's corner
  // rule keeps a 30" approach in front of a door, an opening and a low window
  // alike (base cabinets sit below a door's leaf), and builds its zones here so
  // they carry the same span and passage fields as the generator's.
  const fixed =
    options.frontClearance > 0 ? () => options.frontClearance : null;
  const doorClearanceFor = fixed
    ? fixed
    : options.doorSwingOnly
      ? (op) =>
          isSlidingDoor(op)
            ? OPENING_FRONT_CLEARANCE
            : op?.dimensions?.x || DOOR_FRONT_CLEARANCE
      : () => DOOR_FRONT_CLEARANCE;
  const sources = [
    {
      items: roomData?.doors || [],
      kind: 'door',
      clearanceFor: doorClearanceFor,
    },
    {
      items: roomData?.openings || [],
      kind: 'opening',
      clearanceFor: fixed || (() => OPENING_FRONT_CLEARANCE),
    },
    {
      items: roomData?.windows || [],
      kind: 'window',
      clearanceFor: fixed || (() => WINDOW_FRONT_CLEARANCE),
    },
  ];

  const zones = [];
  for (const { items, kind, clearanceFor } of sources) {
    for (const opening of items) {
      const wallId = opening?.parentWallIds?.[0];
      const wall = wallById.get(wallId);
      if (!wall) continue;
      const axes = getWallAxes(wall);
      if (!axes) continue;
      const center = opening?.center;
      const width = opening?.dimensions?.x;
      if (!center || !width || width <= 0) continue;
      const clearance = clearanceFor(opening);

      // Project the opening center onto the wall axis so the zone's
      // back face sits exactly on the wall surface. (Stored centers can
      // be offset from the wall by a small amount — half the wall
      // thickness — when the slab thickness is baked into the data.)
      const dx = center.x - axes.start.x;
      const dz = center.z - axes.start.z;
      const t = dx * axes.wallDir.x + dz * axes.wallDir.z;
      const perpDist = Math.abs(
        dx * axes.wallNormal.x + dz * axes.wallNormal.z
      );

      // Guard against bad upstream data — sometimes a door / opening is
      // tagged with a parentWallId whose geometry doesn't match the
      // opening's own coordinates (off the wall plane, or wider than
      // the wall itself). Don't project a 54" clearance zone from a
      // wall the opening doesn't actually live on.
      //
      // Tested on the opening's SPAN, not its centre. Callers hand us the
      // walls CLIPPED to the kitchen section, and an opening can straddle a
      // clipped end: after a kitchen-area edit at 50 Park St the section
      // began 2 ft inside a 72" window, so the window's centre projected 20"
      // off the wall — past this guard by less than an inch — and the whole
      // window produced NO zone. A peninsula then ran its seating counter and
      // three barstools straight across the glass, with nothing to object.
      // The same span test is what `getAvailableWallSegments` uses to decide
      // an opening lies "entirely off this wall".
      const halfWidth = width / 2;
      const projectedAlongWall =
        t + halfWidth >= -0.5 && t - halfWidth <= axes.wallLen + 0.5;
      const sitsOnWallPlane = perpDist <= 0.3; // 30 cm — typical wall thickness
      const fitsWithinWall = width <= axes.wallLen + 0.3;
      if (!projectedAlongWall || !sitsOnWallPlane || !fitsWithinWall) continue;

      const surfaceCenter = {
        x: axes.start.x + t * axes.wallDir.x,
        z: axes.start.z + t * axes.wallDir.z,
      };
      // Center the clearance rect in front of the opening: shift the
      // surface center inward by half the clearance depth so
      // calculateRectCorners — which centers around the supplied point —
      // produces a rect with its back face on the wall and front face
      // `clearance` meters into the room.
      const zoneCenter = {
        x: surfaceCenter.x + (clearance / 2) * axes.wallNormal.x,
        z: surfaceCenter.z + (clearance / 2) * axes.wallNormal.z,
      };
      const poly = calculateRectCorners(
        zoneCenter,
        width,
        clearance,
        axes.wallDir,
        axes.wallNormal
      );
      zones.push({
        poly,
        kind,
        openingId: opening.id || null,
        parentWallId: wallId,
        // Does this zone obstruct anything at COUNTER height (~36")?
        // A window whose sill sits above the countertop does not — a base
        // run tucks under it, which is why `TALL_TYPES` above exempts
        // counter-height items from the zone check. Doors and door-less
        // openings run to the floor, so they always block. Consumers that
        // reserve space for LOWER cabinets must honour this; the tall-item
        // checks ignore it. Defaults to blocking whenever the sill can't be
        // judged.
        blocksCounterHeight:
          kind !== 'window' ||
          !sillsAreKnowable ||
          isLowSillWindow(opening, floorY),
        // Along-wall span of the opening, so a consumer can ask how much
        // CLEAR PASSAGE survives an intrusion instead of only whether one
        // happened. A door keeps the all-or-nothing rule (the leaf sweeps
        // its whole width); a door-less opening wider than the passage
        // only has to keep `passageWidth` of itself free — see
        // OPENING_PASSAGE_CLEARANCE.
        //
        // NOT extended to sliding doors, though the same passage argument
        // applies (a slider's panels travel inside the opening, so a 197"
        // glass wall arguably needs a way through rather than 197" x 54" of
        // reserved floor). Tried 2026-09-06 for the Golden West mid-arm holes,
        // when the fill's keep-outs never read `passageWidth`, and it changed
        // nothing. They do now (`openingKeepOuts`, #2822), so a slider is the
        // next case to measure — still unmeasured, so still not extended.
        spanStart: t - width / 2,
        spanEnd: t + width / 2,
        wallDir: axes.wallDir,
        wallStart: axes.start,
        passageWidth:
          kind === 'opening' && width > OPENING_PASSAGE_CLEARANCE
            ? OPENING_PASSAGE_CLEARANCE
            : null,
      });
    }
  }
  return zones;
}

/**
 * Door/opening clearance zones sized for the PENINSULA (and any other
 * counter-height cabinet-run) pre-filter — doors use their true leaf-swing
 * depth (`doorSwingOnly`), not the padded 54" stand-back. A peninsula is
 * counter-height, so it may sit in a door's approach buffer (the leaf never
 * reaches it) but not in the actual swing / doorway passage; see the
 * `openingClearance` catalog entry. SINGLE SOURCE OF TRUTH — every peninsula
 * door check (the layout generator AND the schematic typology explorer) must
 * build its zones through this helper so the two paths can't drift on how
 * strict the door check is.
 */
function getPeninsulaDoorZones(roomData) {
  return getOpeningClearanceZones(roomData, { doorSwingOnly: true });
}

/**
 * A wall-aligned 2D footprint polygon for an object.
 *
 * Named apart from `@shared-lib/geometry/objectFootprint` on purpose: that one
 * orients by the object's OWN `rotation.y`, this one by its parent WALL's axes
 * (falling back to the object's rotation when it has no parent). Two different
 * answers for a fixture whose rotation disagrees with its wall, and while both
 * were called `objectFootprint` you could not tell which a call site meant
 * without opening the file.
 */
function wallAlignedFootprint(obj, wallById) {
  const center = obj?.center;
  const width = obj?.dimensions?.x;
  const depth = obj?.dimensions?.z;
  if (!center || !width || !depth || depth <= 0) return null;

  const parentWallId = obj?.parentWallIds?.[0];
  const wall = parentWallId ? wallById.get(parentWallId) : null;
  const axes = wall ? getWallAxes(wall) : null;
  if (axes) {
    return calculateRectCorners(
      center,
      width,
      depth,
      axes.wallDir,
      axes.wallNormal
    );
  }
  // Fallback: derive direction vectors from the object's own rotation.
  // Kitchen-assembly stores rotation.y as atan2(wallNormal.x, wallNormal.z),
  // so (sin, cos) reproduces wallNormal and the tangent gives wallDir.
  const ry = obj?.rotation?.y || 0;
  const wallNormal = { x: Math.sin(ry), z: Math.cos(ry) };
  const wallDir = { x: -wallNormal.z, z: wallNormal.x };
  return calculateRectCorners(center, width, depth, wallDir, wallNormal);
}

/**
 * Test a single object against a precomputed zone list. Returns the
 * first conflicting zone or null. Same-wall openings are skipped.
 */
/**
 * Widest stretch of an opening left CLEAR by the footprints intruding on it.
 *
 * Each footprint is projected onto the opening's own along-wall axis and
 * clipped to its span; the gaps between the merged projections (plus the two
 * ends) are the passages that survive. A zone carrying `passageWidth` only has
 * to keep that much of itself free, so this is what decides it — the
 * equivalent of sliding a passage-width rectangle within the opening looking
 * for a clear spot, computed directly instead of searched.
 *
 * SINGLE SOURCE: both the layout validator (which aggregates every intruding
 * rect in a candidate) and the per-object `objectConflictsWithZones` (which
 * asks the same question of one footprint) go through this, so the gate and
 * the materializer cannot disagree about what a wide opening allows. They
 * agree because a candidate whose SINGLE object already eats the passage is
 * rejected by the validator and never reaches materialization.
 *
 * @param {Object} zone - carries spanStart/spanEnd/wallDir/wallStart
 * @param {Array<Array<{x:number,z:number}>>} footprints - intruding polygons
 * @returns {number} widest clear run in meters
 */
function widestClearRun(zone, footprints) {
  const { spanStart, spanEnd } = zone;
  const blocks = [];
  for (const corners of footprints) {
    const b = spanTakenBy(zone, corners);
    if (b) blocks.push(b);
  }
  if (!blocks.length) return spanEnd - spanStart;
  blocks.sort((a, b) => a.lo - b.lo);
  let widest = 0;
  let cursor = spanStart;
  for (const b of blocks) {
    if (b.lo > cursor) widest = Math.max(widest, b.lo - cursor);
    cursor = Math.max(cursor, b.hi);
  }
  return Math.max(widest, spanEnd - cursor);
}

/**
 * The stretch of the opening's own span a footprint covers, projected onto the
 * opening's along-wall axis and clipped to the span — `{ lo, hi }`, or null
 * when it covers none of it.
 */
function spanTakenBy(zone, corners) {
  const { wallDir, wallStart, spanStart, spanEnd } = zone;
  let lo = Infinity;
  let hi = -Infinity;
  for (const c of corners) {
    const t = (c.x - wallStart.x) * wallDir.x + (c.z - wallStart.z) * wallDir.z;
    if (t < lo) lo = t;
    if (t > hi) hi = t;
  }
  lo = Math.max(lo, spanStart);
  hi = Math.min(hi, spanEnd);
  return hi > lo ? { lo, hi } : null;
}

/**
 * THE passage test: does a wide door-less opening still have its way through
 * with these footprints standing in front of it? Only a zone carrying
 * `passageWidth` can — a door, a window or a narrow opening is never judged on
 * what is left of it, so the answer for one is always false.
 *
 * One test for every asker: the layout validator (every intruding piece of a
 * candidate), `objectConflictsWithZones` (one tall object), and
 * `openingKeepOuts` (the generator's envelope and materializer, the run fill
 * and through it the run-end drag). Asked with the same pieces standing, they
 * cannot disagree about what a wide opening allows.
 */
function leavesPassage(zone, footprints) {
  if (!zone?.passageWidth) return false;
  return (
    widestClearRun(zone, footprints) >= zone.passageWidth - OVERLAP_TOLERANCE
  );
}

/**
 * Does this object reach the height a clearance zone protects?
 *
 * `TALL_TYPES` answers by type, which works for everything whose type says how
 * tall it is. `corner-cabinet` does not: it is BOTH the lower L and the upper
 * one, told apart only by `dimensions.isUpper`. So the type test alone let an
 * upper corner cabinet sit in a door's swing where a plain `upper-cabinet` in
 * the same place is rejected. Counter-height corner units stay exempt, like
 * every other counter-height item (see the `TALL_TYPES` docblock).
 */
function reachesClearanceHeight(obj) {
  const type = (obj?.type || '').toLowerCase();
  if (type === 'corner-cabinet') return obj?.dimensions?.isUpper === true;
  return TALL_TYPES.has(type);
}

function objectConflictsWithZones(obj, zones, wallById) {
  if (!zones || zones.length === 0) return null;
  if (!reachesClearanceHeight(obj)) return null;
  const footprint = wallAlignedFootprint(obj, wallById);
  if (!footprint) return null;
  const objWallId = obj?.parentWallIds?.[0];
  for (const zone of zones) {
    if (objWallId && objWallId === zone.parentWallId) continue;
    if (!doPolygonsOverlap(footprint, zone.poly, OVERLAP_TOLERANCE)) continue;
    // A wide door-less opening only has to keep a PASSAGE clear, so one
    // object grazing its edge is not a conflict — same rule the layout
    // validator applies, through the same helper.
    if (leavesPassage(zone, [footprint])) continue;
    return zone;
  }
  return null;
}

/**
 * Inspect every materialized object against every opening clearance
 * zone. Returns the first detected conflict (or null when the layout is
 * clear). Conflicts on the same wall as the opening are ignored — those
 * are caught upstream by wallSegments and only happen when a cabinet
 * literally sits inside the opening width, which it cannot.
 */
function checkOpeningClearanceConflict(objects, roomData) {
  const zones = getOpeningClearanceZones(roomData);
  if (zones.length === 0) return null;
  const wallById = new Map((roomData?.walls || []).map((w) => [w.id, w]));

  for (const obj of objects) {
    const zone = objectConflictsWithZones(obj, zones, wallById);
    if (zone) {
      return {
        objectId: obj.id || null,
        objectType: (obj?.type || '').toLowerCase(),
        openingId: zone.openingId,
        openingKind: zone.kind,
        openingParentWallId: zone.parentWallId,
      };
    }
  }
  return null;
}

/**
 * Project an opening clearance zone onto a wall's parameter axis and
 * return the wall-param range that a 24"-deep cabinet would intrude
 * upon. Returns `null` when the zone doesn't overlap the wall's
 * cabinet-depth strip (so it produces no blocker on this wall).
 *
 * The cabinet-depth strip is the rectangle bounded by the wall on one
 * side and `cabinetDepth` perpendicular to the wall on the other.
 * If the zone's polygon overlaps that strip, the param range it covers
 * (along the wall) is returned as a fill-pass blocker so the
 * lower/upper-fill loops don't try to place a cabinet that would clip
 * into the zone.
 *
 * @param {Object} wall
 * @param {{poly: Array<{x:number,z:number}>}} zone
 * @param {number} cabinetDepth - meters
 * @returns {{start: number, end: number} | null}
 */
function projectZoneToWallBlocker(wall, zone, cabinetDepth) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const { start, wallDir, wallNormal, wallLen } = axes;
  let pMin = Infinity;
  let pMax = -Infinity;
  let dMin = Infinity;
  let dMax = -Infinity;
  for (const v of zone.poly) {
    const dx = v.x - start.x;
    const dz = v.z - start.z;
    const param = dx * wallDir.x + dz * wallDir.z;
    const depth = dx * wallNormal.x + dz * wallNormal.z;
    if (param < pMin) pMin = param;
    if (param > pMax) pMax = param;
    if (depth < dMin) dMin = depth;
    if (depth > dMax) dMax = depth;
  }
  // Cabinet-depth strip is depth ∈ [0, cabinetDepth]. Skip if zone is
  // entirely behind the wall or entirely past the cabinet's front face.
  if (dMax <= OVERLAP_TOLERANCE) return null;
  if (dMin >= cabinetDepth - OVERLAP_TOLERANCE) return null;
  // Clamp param range to the wall's extent.
  const clampedStart = Math.max(0, pMin);
  const clampedEnd = Math.min(wallLen, pMax);
  if (clampedEnd <= clampedStart + OVERLAP_TOLERANCE) return null;
  return { start: clampedStart, end: clampedEnd };
}

/**
 * The cabinet band a blocker range stands for: `depth` out from the wall face
 * over `[start, end]` of the wall — what a run reaching into the zone puts
 * there.
 */
function bandFootprint(wall, range, depth) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const mid = (range.start + range.end) / 2;
  const center = {
    x: axes.start.x + mid * axes.wallDir.x + (depth / 2) * axes.wallNormal.x,
    z: axes.start.z + mid * axes.wallDir.z + (depth / 2) * axes.wallNormal.z,
  };
  return calculateRectCorners(
    center,
    range.end - range.start,
    depth,
    axes.wallDir,
    axes.wallNormal
  );
}

/** A blocker range cut to a band's `span`; null when nothing of it is left. */
function withinSpan(range, span) {
  if (!span) return range;
  const start = Math.max(range.start, span.lo);
  const end = Math.min(range.end, span.hi);
  return end > start + OVERLAP_TOLERANCE ? { start, end } : null;
}

/**
 * Where no cabinet may stand on each wall because of an opening on ANOTHER
 * wall — THE keep-out rule for the clear floor in front of an opening, shared
 * by the generator's wall envelope, its materializer and the run fill (and,
 * through the fill, the run-end drag).
 *
 * Each zone that reaches a band's cabinet depth is a keep-out on that band's
 * wall (`projectZoneToWallBlocker`), with one exception: a wide door-less
 * opening only has to keep a WAY THROUGH (`passageWidth`), so a band may reach
 * into its zone when — with everything already `standing` in front of it and
 * every band admitted before it — the passage survives (`leavesPassage`).
 * Bands are admitted smallest intrusion first (ties by wall id), so the answer
 * depends on the room and never on which wall is asked first, and a band that
 * would close the passage keeps the keep-out.
 *
 * The generator asks with every cabinet wall of a layout and the candidate's
 * appliances standing: it is deciding the whole layout at once. The run fill asks with every
 * wall-backed segment of its run and the room's OTHER runs standing as they
 * are — which is the generator's decision read back, since a band admitted
 * alongside the others is admitted again when they stand, and one refused is
 * refused again. A run's segments are asked together because they are built
 * together: a U whose two arms flank an archway is one run, and asked one arm
 * at a time each arm took the passage as if the other were not there.
 *
 * A band that stops short of its wall's ends says so (`span`): only the part
 * of the zone over its span is the band's to take, and a band whose span
 * misses the zone neither takes the passage nor needs a keep-out.
 *
 * Doors, windows and narrow openings are unchanged: always a keep-out.
 *
 * @param {Array<{wall: Object, depth: number, decides?: boolean,
 *   span?: {lo: number, hi: number}}>} bands
 *   walls that may carry cabinets, at the depth they carry them; a band with
 *   `decides: false` gets the plain keep-out and is never admitted; `span` is
 *   the stretch of the wall (wall params) the band covers, when not all of it
 * @param {Array<Object>} zones `getOpeningClearanceZones` output
 * @param {{standing?: Array<Array<{x:number,z:number}>>}} [opts]
 *   footprints already in the room that are not being decided
 * @returns {Array<Array<{start: number, end: number, zone: Object}>>}
 *   keep-outs per band, in `bands` order
 */
function openingKeepOuts(bands, zones, { standing = [] } = {}) {
  const out = bands.map(() => []);
  for (const zone of zones || []) {
    const contenders = [];
    bands.forEach((band, i) => {
      if (!band?.wall || band.wall.id === zone.parentWallId) return;
      const blk = projectZoneToWallBlocker(band.wall, zone, band.depth);
      if (!blk) return;
      const reach = withinSpan(blk, band.span);
      if (!reach) return;
      const strip =
        zone.passageWidth && band.decides !== false
          ? bandFootprint(band.wall, reach, band.depth)
          : null;
      const taken = strip && spanTakenBy(zone, strip);
      if (!taken) {
        out[i].push({ ...blk, zone });
        return;
      }
      contenders.push({ i, blk, strip, takes: taken.hi - taken.lo });
    });
    if (!contenders.length) continue;
    const admitted = standing.filter((fp) =>
      doPolygonsOverlap(fp, zone.poly, OVERLAP_TOLERANCE)
    );
    contenders.sort(
      (a, b) =>
        a.takes - b.takes ||
        String(bands[a.i].wall.id).localeCompare(String(bands[b.i].wall.id))
    );
    for (const c of contenders) {
      if (leavesPassage(zone, [...admitted, c.strip])) admitted.push(c.strip);
      else out[c.i].push({ ...c.blk, zone });
    }
  }
  return out;
}

module.exports = {
  OVERLAP_TOLERANCE,
  BLOCKING_TYPES,
  TALL_TYPES,
  isSlidingDoor,
  getOpeningClearanceZones,
  getPeninsulaDoorZones,
  projectZoneToWallBlocker,
  wallAlignedFootprint,
  objectConflictsWithZones,
  widestClearRun,
  leavesPassage,
  openingKeepOuts,
  checkOpeningClearanceConflict,
};
